import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ApplicationsContract } from '@terra/shared-contracts-applications';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import { terraApiWrapper } from '../../utils/terraApiWrapper';
import { DYNAMIC_APPLICATION_SCAN_AUTOMATION_QUERY_KEY } from './useDynamicApplicationScanAutomation';

const TRIGGER_FAILED_MESSAGE = 'Failed to trigger dynamic application scan';

const readErrorMessage = (body: unknown): string | undefined => {
  if (typeof body !== 'object' || body == null) {
    return undefined;
  }

  const { message } = body as Record<string, unknown>;

  return typeof message === 'string' && message.length > 0
    ? message
    : undefined;
};

const triggerDynamicApplicationScan = async ({
  applicationId,
}: {
  applicationId: string;
}) => {
  const result = await terraApiWrapper<
    typeof ApplicationsContract.triggerDynamicApplicationScan
  >({
    endpointFn: () =>
      applicationsClient.triggerDynamicApplicationScan({
        params: { id: applicationId },
      }),
    errorHandler: (response) => {
      throw new Error(
        readErrorMessage(response.body) ?? TRIGGER_FAILED_MESSAGE,
      );
    },
  });

  if (result == null || (result.run == null && result.directoryScan == null)) {
    throw new Error(TRIGGER_FAILED_MESSAGE);
  }

  return result;
};

export const useTriggerDynamicApplicationScan = ({
  applicationId,
}: {
  applicationId: string;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => triggerDynamicApplicationScan({ applicationId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          DYNAMIC_APPLICATION_SCAN_AUTOMATION_QUERY_KEY,
          applicationId,
        ],
      });
    },
  });
};
